import { useMemo } from 'react';
import cx from 'classnames';
import { FaPlus, FaTimes, FaEyeSlash } from 'react-icons/fa';

import style from './product-card.module.scss';
import { useCartContext } from '../../../context/CartContext/CartContext';
import { ProductAttribute, ProductFeature } from '../../../models/Product/contracts';

// Definišemo interfejs za props komponente
interface ProductCardProps {
  id: number;
  name: string;
  image: string;
  price: number;
  url: string;
  listView: boolean;
  attributes?: ProductAttribute[];
  features?: ProductFeature[];
  isAvailable?: boolean;
}

// Definišemo tip za kombinovane detalje
type CombinedDetail = {
  name: string;
  value: string;
};

function ProductCard({
  id,
  image,
  name,
  price,
  url,
  listView,
  attributes,
  features,
  isAvailable = true,
}: ProductCardProps) {
  const { cart, cartAddProduct, cartRemoveProduct } = useCartContext();
  const isProductInCart = cart?.products.some((p) => p.id === id);

  /**
   * Memoizovana vrednost koja kombinuje atribute i karakteristike
   */
  const allDetails = useMemo(() => {
    const combined: CombinedDetail[] = [];
    const processItems = (items?: (ProductAttribute | ProductFeature)[]) => {
      if (!items) return;
      items.forEach(item => {
        if (item.name && item.value) {
          combined.push({ name: item.name, value: item.value });
        }
      });
    };
    processItems(attributes);
    processItems(features);
    return combined;
  }, [attributes, features]);

  // Handler za dodavanje/uklanjanje iz korpe
  const handleCartAction = async () => {
    try {
      if (isProductInCart) {
        await cartRemoveProduct(id);
      } else {
        await cartAddProduct(id, 1);
      }
    } catch (error) {
      console.error('Greška pri ažuriranju korpe:', error);
    }
  };

  // Handler za navigaciju
  const handleNavigation = () => {
    window.open(url, '_blank');
  };

  // --- Grid View ---
  if (!listView) {
    return (
      <div className={cx(style.productCard, { [style.unavailable]: !isAvailable })}>
        <div className={cx(style.imageWrapper, { [style.inCart]: isProductInCart })} onClick={handleNavigation}>
          <img src={image} alt={name} onError={(e) => { (e.target as HTMLImageElement).src = 'https://placehold.co/300x400/2c2c2c/a0a0a0?text=Image+Not+Found'; }} />
          {!isAvailable && <div className={style.unavailableOverlay}><span>Nedostupno</span></div>}
        </div>
        <div className={style.contentWrapper}>
          <div className={style.mainContent}>
            <div className={style.title} onClick={handleNavigation}>{name}</div>
            <div className={style.price}>{price.toLocaleString()}&nbsp;RSD</div>
          </div>
          <div className={style.actionWrapper} title={isAvailable ? (isProductInCart ? "Ukloni" : "Dodaj") : "Nedostupno"}>
            <button onClick={handleCartAction} disabled={!isAvailable}>
              {isAvailable ? (isProductInCart ? <FaTimes /> : <FaPlus />) : <FaEyeSlash />}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- List View ---
  return (
    <div className={cx(style.productCard, style.list, { [style.unavailable]: !isAvailable })}>
      <div className={cx(style.imageWrapper, { [style.inCart]: isProductInCart })} onClick={handleNavigation}>
        <img src={image} alt={name} onError={(e) => { (e.target as HTMLImageElement).src = 'https://placehold.co/300x400/2c2c2c/a0a0a0?text=Image+Not+Found'; }} />
        {!isAvailable && <div className={style.unavailableOverlay}><span>Nedostupno</span></div>}
      </div>
      <div className={style.detailsWrapper}>
        <div className={style.topRow}>
          <div className={style.mainContent}>
            <div className={style.title} onClick={handleNavigation}>{name}</div>
            <div className={style.price}>{price.toLocaleString()}&nbsp;RSD</div>
          </div>
          <div className={style.actionWrapper} title={isAvailable ? (isProductInCart ? "Ukloni" : "Dodaj") : "Nedostupno"}>
            <button onClick={handleCartAction} disabled={!isAvailable}>
              {isAvailable ? (isProductInCart ? <FaTimes /> : <FaPlus />) : <FaEyeSlash />}
            </button>
          </div>
        </div>
        {allDetails.length > 0 && (
          <div className={style.attributesWrapper}>
            <ul className={style.attributesList}>
              {allDetails.map(detail => <li key={`${detail.name}-${detail.value}`}><strong>{detail.name}:</strong> {detail.value}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductCard;